import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectOptions } from 'src/store/optionSlice';
import { useFetchUserProfileQuery, UserEntity } from 'src/store/userApi';
import {
  EventUserEntity,
  useUpdateEventUserMutation,
} from 'src/store/eventApi';
import { getAttendeeDisplayName } from 'src/util';
import {
  buildOptionsMap,
  decodeFieldValue,
  OptionLike,
} from '../user-profile/profileFieldUtils';
import UserProfileReadOnly from '../user-profile/userProfileReadOnly';
import { Modal } from 'src/ui/Modal';
import { Button } from 'src/ui/Button';
import { Field } from 'src/ui/Field';
import { Input } from 'src/ui/Input';
import { Spinner } from 'src/ui/Spinner';
import { useToast } from 'src/ui/Toast';

interface AttendeeDetailModalProps {
  eventUser: EventUserEntity | null;
  isOpen: boolean;
  onClose: () => void;
}

const renderHeader = (
  user: UserEntity,
  eventUser: EventUserEntity,
  optionsMap: Record<string, OptionLike>
) => {
  const name = getAttendeeDisplayName(eventUser) || user.email;
  const infoParts: string[] = [];

  if (user.profile?.gender) {
    infoParts.push(decodeFieldValue('gender', user.profile.gender, optionsMap));
  }
  if (user.profile?.age) {
    infoParts.push(String(user.profile.age));
  }

  const locationParts: string[] = [];
  (['city', 'state', 'country'] as const).forEach((k) => {
    const v = user.profile?.[k];
    if (v) locationParts.push(decodeFieldValue(k, v, optionsMap));
  });
  if (locationParts.length) infoParts.push(locationParts.join(', '));

  return (
    <div
      className="row gap-3"
      style={{ justifyContent: 'space-between', alignItems: 'center' }}
    >
      <div>
        <div style={{ fontWeight: 600, fontSize: 'var(--fs-md)' }}>{name}</div>
        {infoParts.length > 0 && (
          <div
            className="text-secondary"
            style={{ fontSize: 'var(--fs-sm)', marginTop: 2 }}
          >
            {infoParts.join(' · ')}
          </div>
        )}
      </div>
      <Link to={`/user-profile/${user.id}`} target="_blank">
        <Button variant="secondary" size="sm" rightIcon="external-link">
          Edit Profile
        </Button>
      </Link>
    </div>
  );
};

const renderPhotos = (user: UserEntity) => {
  if (!user.photos?.length) {
    return (
      <div
        style={{
          padding: 16,
          textAlign: 'center',
          color: 'var(--text-muted)',
          fontSize: 'var(--fs-sm)',
          background: 'var(--bg-muted)',
          borderRadius: 'var(--radius-md)',
        }}
      >
        No photos uploaded
      </div>
    );
  }
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 8,
      }}
    >
      {user.photos.slice(0, 4).map((photo, index) => (
        <div
          key={photo.id}
          style={{
            position: 'relative',
            paddingBottom: '100%',
            overflow: 'hidden',
            borderRadius: 'var(--radius-md)',
            background: 'var(--bg-muted)',
          }}
        >
          {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
          <img
            src={photo.lImage?.url || photo.mImage?.url || photo.sImage?.url}
            alt={`Profile image ${index + 1}`}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        </div>
      ))}
    </div>
  );
};

const AttendeeDetailModal = ({
  eventUser,
  isOpen,
  onClose,
}: AttendeeDetailModalProps) => {
  const userId = eventUser?.userId;
  const { data: user, isFetching } = useFetchUserProfileQuery(userId ?? '', {
    skip: !isOpen || !userId,
  });

  const allOptions = useSelector(selectOptions);
  const optionsMap = useMemo<Record<string, OptionLike>>(
    () => (Object.keys(allOptions).length ? buildOptionsMap(allOptions) : {}),
    [allOptions]
  );

  const toast = useToast();
  const [updateEventUser, { isLoading: saving }] = useUpdateEventUserMutation();
  const [nicknameDraft, setNicknameDraft] = useState(
    eventUser?.eventNickname ?? ''
  );

  useEffect(() => {
    setNicknameDraft(eventUser?.eventNickname ?? '');
  }, [eventUser?.id, eventUser?.eventNickname]);

  const trimmedDraft = nicknameDraft.trim();
  const savedNickname = eventUser?.eventNickname ?? '';
  const dirty = trimmedDraft !== savedNickname;

  const onSaveNickname = async () => {
    if (!eventUser) return;
    try {
      await updateEventUser({
        eventId: eventUser.eventId,
        eventUserId: eventUser.id,
        eventNickname: trimmedDraft === '' ? null : trimmedDraft,
      }).unwrap();
      toast({ type: 'success', title: 'Nickname saved' });
    } catch (e: any) {
      toast({
        type: 'error',
        title: 'Failed to save nickname',
        message: e?.data?.message || e?.message,
      });
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={isFetching && !user ? 'Loading…' : undefined}
      width={680}
    >
      {isFetching && !user ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 40,
          }}
        >
          <Spinner size="lg" />
        </div>
      ) : !user || !eventUser ? null : (
        <div
          style={{ height: 'min(70vh, calc(100vh - 8rem))', overflowY: 'auto' }}
        >
          {/* Header */}
          <div style={{ marginBottom: 16 }}>
            {renderHeader(user, eventUser, optionsMap)}
          </div>

          <div className="divider" style={{ margin: '0 0 16px' }} />

          {/* Nickname */}
          <Field
            label="Nickname for this event"
            hint="Shown on the print card and match results. Leave blank to use the profile name."
          >
            <div className="row gap-2">
              <Input
                value={nicknameDraft}
                onChange={(e) => setNicknameDraft(e.target.value)}
                placeholder={user.profile?.name ?? 'e.g. Ali'}
                maxLength={64}
              />
              <Button
                onClick={onSaveNickname}
                loading={saving}
                disabled={!dirty}
              >
                Save
              </Button>
            </div>
          </Field>

          {/* Photos */}
          <div style={{ marginTop: 20 }}>
            <div
              style={{
                fontWeight: 600,
                fontSize: 'var(--fs-sm)',
                marginBottom: 8,
              }}
            >
              Photos
            </div>
            {renderPhotos(user)}
          </div>

          {/* Profile readonly view */}
          <div style={{ marginTop: 20 }}>
            <UserProfileReadOnly user={user} />
          </div>
        </div>
      )}
    </Modal>
  );
};

export default AttendeeDetailModal;
